import { postData } from "@/request/api";
import { useMutation } from "@tanstack/react-query";

/* AI verdict comparing a set of properties */
export const useCompareVerdict = () => {
  return useMutation({
    mutationFn: async (data: { propertyIds: string[]; locale: string }) => {
      const response = await postData("ai/compare", data);
      return response.data as { verdict: string };
    },
  });
};

/* Chat with a property (it role-plays as itself) */
export const useAskProperty = () => {
  return useMutation({
    mutationFn: async (data: {
      slug: string;
      messages: { role: "user" | "assistant"; content: string }[];
      locale: string;
    }) => {
      const response = await postData("ai/ask-property", data);
      return response.data as { message: string };
    },
  });
};

/* Fair-price estimate + risk verdict (grounded on real comparables) */
export const useFairPrice = () => {
  return useMutation({
    mutationFn: async (data: {
      region: string;
      propertyType: string;
      area: number;
      purpose: "buy" | "rent";
      rentType?: "daily" | "yearly";
      price?: number;
    }) => {
      const response = await postData("ai/fair-price", data);
      return response.data as {
        fairPrice: number;
        low: number;
        high: number;
        pricePerM?: number;
        risk: "low" | "medium" | "high";
        verdict: string;
        comparables?: number;
        medianPricePerM?: number;
      };
    },
  });
};

/* Commute distance + driving time from a property to a destination */
export const useCommute = () => {
  return useMutation({
    mutationFn: async (data: {
      fromLat: number;
      fromLng: number;
      destination: string;
    }) => {
      const response = await postData("geo/commute", data);
      return response.data as {
        distanceKm: number;
        durationMin: number;
        destinationLabel: string;
        approx: boolean;
      };
    },
  });
};
